import { msgs } from '../../utilities/messages';

import { MFA_ERROR, MFA_HTTP_STATUS, MFA_OUTCOME } from './constants';

/**
 * Maps an HTTP status from an MFA-gated operation to the outcome that drives the
 * challenge flow. See the "Native SSH - MFA" spec HTTP status contract.
 * @param {number} status - the response status code
 * @returns {string} one of MFA_OUTCOME
 */
export const outcomeForStatus = (status) => {
  // HttpService auto-parses 200 JSON responses, so the resolved value is a
  // plain object without a `status` property. Treat a missing/undefined status
  // the same as an explicit 200 — the request already succeeded.
  if (status === undefined || status === null) return MFA_OUTCOME.SUCCESS;

  switch (status) {
    case MFA_HTTP_STATUS.SUCCESS:
      return MFA_OUTCOME.SUCCESS;
    case MFA_HTTP_STATUS.REQUIRED:
      return MFA_OUTCOME.REQUIRED;
    case MFA_HTTP_STATUS.INVALID:
      return MFA_OUTCOME.INVALID;
    case MFA_HTTP_STATUS.FORBIDDEN:
    case MFA_HTTP_STATUS.SERVICE_UNAVAILABLE:
      return MFA_OUTCOME.TERMINAL;
    default:
      return MFA_OUTCOME.PASSTHROUGH;
  }
};

/**
 * Safely reads the `error` code from an MFA error response body
 * ({ error, message, status }). Never throws — returns null if the body is
 * missing or unreadable, or if the response has already been consumed.
 * @param {Response} response - a fetch Response for a non-2xx status
 * @returns {Promise<string|null>} the error code, or null
 */
export const readMfaError = async (response) => {
  if (!response || typeof response.clone !== 'function') return null;

  try {
    // Clone so a caller that also reads the body is unaffected.
    const data = await response.clone().json();

    return data?.error ?? null;
  } catch {
    return null;
  }
};

// Error code -> localized message key.
const ERROR_MESSAGE = {
  [MFA_ERROR.INVALID]: msgs.pam.mfa.invalidCode,
  [MFA_ERROR.ACCOUNT_LOCKED]: msgs.pam.mfa.accountLocked,
  [MFA_ERROR.NOT_REGISTERED]: msgs.pam.mfa.notRegistered,
  [MFA_ERROR.USER_NOT_FOUND]: msgs.pam.mfa.userNotFound,
  [MFA_ERROR.SERVICE_UNAVAILABLE]: msgs.pam.mfa.serviceUnavailable,
};

/**
 * Resolves the user-facing error message for an MFA error code, falling back to
 * a generic message for unknown codes.
 * @param {Function} localize - the localize function from useLocalize
 * @param {string|null} errorCode - one of MFA_ERROR, or null
 * @returns {string} the localized error message
 */
export const getMfaErrorMessage = (localize, errorCode) =>
  localize(ERROR_MESSAGE[errorCode] ?? msgs.pam.mfa.genericError);
